import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Stack } from '@integra/ui';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Screen-level error boundary.
 *
 * Without one, a render error unmounts the whole tree and the operator gets a
 * BLANK PAGE — indistinguishable from a hung request, and the exact "silent
 * failure" the estate's fail-loud rule exists to prevent (§4.8). A crash should
 * say what broke and leave the rest of the app usable.
 *
 * Must be a class: React exposes no hook equivalent of componentDidCatch.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the browser's own error reporting rather than console.* —
    // §21.2 prohibits console logging, and this keeps the stack recoverable.
    queueMicrotask(() => {
      throw Object.assign(error, { componentStack: info.componentStack });
    });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-10">
        <Stack gap="md">
          <Alert tone="error" title="This screen failed to render">
            {error.message}
          </Alert>
          <p className="text-sm text-ink-muted">
            The rest of the application is unaffected. If this persists, the correlation id of the
            last failed request is in the network log.
          </p>
          <div>
            <Button variant="secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          </div>
        </Stack>
      </div>
    );
  }
}
